import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CustomerFieldsForm } from "@/components/CustomerFieldsForm";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Building2, ChevronLeft, RefreshCcw } from "lucide-react";

function fmtDate(s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return s; }
}

export default function CustomerProfilePage() {
    const [profile, setProfile] = useState(null);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const load = async () => {
        setLoading(true);
        setNotFound(false);
        try {
            const { data: p } = await api.get("/customer/profile");
            setProfile(p);
            setData(p);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = {};
            ["company_name","dba","company_address","ein_tax_id","usdot_number","company_phone",
             "billing_contact_name","billing_contact_email","billing_contact_phone",
             "guarantor_name","guarantor_ssn","guarantor_home_address","guarantor_dob",
             "guarantor_phone","guarantor_email",
             "agent_name","agent_company","agent_email","agent_phone",
             "trade_reference_1","trade_reference_2","trade_reference_3","bank_reference"].forEach(k => {
                payload[k] = data?.[k] ?? null;
            });
            const { data: p } = await api.patch("/customer/profile", payload);
            setProfile(p);
            setData(p);
            toast.success("Profile saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (<DashboardShell><p className="text-sm text-slate-500" data-testid="profile-loading">Loading profile…</p></DashboardShell>);

    if (notFound) {
        return (
            <DashboardShell>
                <Link to="/customer" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"><ChevronLeft size={14} /> Dashboard</Link>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your company profile</h1>
                <Card className="mt-6 border-dashed" data-testid="profile-empty">
                    <CardContent className="p-8 text-center">
                        <div className="mx-auto h-12 w-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400">
                            <Building2 size={20} />
                        </div>
                        <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No company profile yet</h3>
                        <p className="mt-2 max-w-md mx-auto text-sm text-slate-600">
                            Your profile is created automatically once an admin approves your first rental application.
                        </p>
                    </CardContent>
                </Card>
            </DashboardShell>
        );
    }

    return (
        <DashboardShell>
            <Link to="/customer" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"><ChevronLeft size={14} /> Dashboard</Link>
            <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">Your company profile</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                {profile.company_name || "Company profile"}
            </h1>
            <div className="mt-3 rounded-md bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 flex items-start gap-3" data-testid="profile-banner">
                <RefreshCcw size={16} className="shrink-0 mt-0.5" />
                <div>
                    <p className="font-medium">This profile is reused for new applications.</p>
                    <p className="text-blue-900/70 mt-0.5">Last verified {fmtDate(profile.updated_at)}.</p>
                </div>
            </div>

            <Card className="mt-6">
                <CardContent className="p-6 space-y-4">
                    <CustomerFieldsForm data={data} onChange={setData} prefix="customer-profile" />
                    <div className="flex gap-2 justify-end">
                        <Button onClick={onSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="profile-save">
                            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
